import {
    type MoneyMap,
    MoneyMapAdvisoryMode,
    MoneyMapAuthorType,
} from '@fruitful/types';

import type { User } from '../../user/types/User';

type MoneyMapAuthorshipFields = Pick<
    MoneyMap,
    'createdByType' | 'createdByUserId' | 'updatedAt' | 'advisoryMode'
>;

type AuthorshipUser = Pick<User, 'id' | 'firstName' | 'partnerMember'>;

const resolveAuthorLabel = (
    moneyMap: MoneyMapAuthorshipFields,
    user: AuthorshipUser,
): string => {
    if (moneyMap.createdByType === MoneyMapAuthorType.MEMBER) {
        if (
            moneyMap.createdByUserId == null ||
            moneyMap.createdByUserId === user.id
        ) {
            return 'you';
        }

        if (moneyMap.createdByUserId === user.partnerMember?.id) {
            return user.partnerMember.firstName ?? 'your partner';
        }

        return 'a household member';
    }

    // GUIDE, or null (pre-authorship maps)
    return 'your Guide';
};

/**
 * Attribution line for Previous Money Maps list/detail.
 * e.g. "Updated by you", "Created by your Guide", "Created during onboarding"
 */
export const getMoneyMapAuthorshipLabel = (
    moneyMap: MoneyMapAuthorshipFields,
    user: AuthorshipUser,
): string => {
    // Goyo CMM maps are generated from intake and never pass a guide. Other
    // CMM maps are drafts a guide reviews, so they stay attributed to the
    // guide. Archiving a Goyo map refreshes updatedAt, so the Updated/Created
    // verb would describe that archive, not an author.
    if (
        moneyMap.createdByType ===
            MoneyMapAuthorType.CONVERSATIONAL_ONBOARDING &&
        moneyMap.advisoryMode === MoneyMapAdvisoryMode.ROBO_ADVISOR_NO_IM
    ) {
        return 'Created during onboarding';
    }

    const verb = moneyMap.updatedAt != null ? 'Updated' : 'Created';
    return `${verb} by ${resolveAuthorLabel(moneyMap, user)}`;
};
